import { defineStore } from "pinia";
import { TrackableEvent } from "@/analytics/TrackableEvents";
import { useAnalytics } from "@/plugins/analytics";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface InstalledApp {
  id: string | null;
  platform: string;
  url: string | null;
  version: string | null;
}

declare global {
  interface Window {
    pwa: null | BeforeInstallPromptEvent;
    PagSeguro: PagSeguroSDK;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }

  interface Navigator {
    getInstalledRelatedApps(): Promise<InstalledApp[]>;
  }
}

export interface AppState {
  version: string;
  commit: string;
  env: string;
  pwa: {
    enabled: boolean;
    installed: boolean;
    event: BeforeInstallPromptEvent | null;
  };
  cookies: {
    prompted: boolean;
    allowed: boolean;
  };
}

export const useAppStore = defineStore("app", {
  state: (): AppState => ({
    version: import.meta.env.__APP_VERSION__,
    commit: import.meta.env.__COMMIT__.replace(/(\r\n|\n|\r)/gm, ""),
    env: import.meta.env.VITE_APP_ENV,
    pwa: {
      enabled: true,
      installed: false,
      event: null,
    },
    cookies: {
      prompted: false,
      allowed: false,
    },
  }),

  actions: {
    async promptPwaInstall(): Promise<void> {
      if (window.pwa == null) return;

      window.pwa.prompt();

      const { outcome } = await window.pwa.userChoice;

      // Dispatch analytics event for tracking downloads
      if (outcome === "accepted") {
        useAnalytics().collect(TrackableEvent.AppDownload, { params: { standalone: "true" } });
      }
    },

    setCookieConsent(value: boolean): void {
      this.cookies.prompted = true;
      this.cookies.allowed = value;
    },

    updateAppInfo(): void {
      this.version = import.meta.env.__APP_VERSION__;
      this.commit = import.meta.env.__COMMIT__.replace(/(\r\n|\n|\r)/gm, "");
      this.env = import.meta.env.VITE_APP_ENV;
    },
  },

  persist: true,
});

// Setup PWA event listener
window.addEventListener("beforeinstallprompt", (e: BeforeInstallPromptEvent) => {
  e.preventDefault();
  window.pwa = e;
});

// Setup installed apps check
async function setup() {
  if ("getInstalledRelatedApps" in window.navigator) {
    await window.navigator.getInstalledRelatedApps();
  }
}

setup();
